import { type FieldValues } from 'react-hook-form';

import { formInitialValues } from '../components/Items/constants';

import {
  type IAdditionalFieldConfig,
  type IAdditionalFieldsConfigMap,
  type IAttachedDocumentConfig,
  type IAttachedDocumentResponse,
  type IItemResponse,
  type ILicenseTypeResponse,
  type ILpcoResponse,
} from '../types/response';
import {
  ArrayFields,
  AttachedDocumentsTabFields,
  BeneficiaryFields,
  ConsignmentTabFields,
  HeaderTabFields,
  ItemsTabFields,
  NamesAndPartiesTabFields,
} from '../types/fieldEnums';
import { type IAdditionalField, type IDBOptionAdditionalField } from '../types/request';
import { FieldPrefix } from '../constants/fieldPrefixes';
import { DataType } from '../types/enums';

import groupByBillNumber from './groupByBillNumber';
import getSelectValue from './getSelectValue';

const getAdditionalFieldsFormValues = (additionalFields: IAdditionalField[], config: IAdditionalFieldsConfigMap) =>
  additionalFields?.reduce((acc, field) => {
    const { fieldId, fieldValue } = field;
    const key = `${FieldPrefix.additionalField}${fieldId}`;

    if (config[key]?.dataType === DataType.OPTIONS_FROM_DATABASE) {
      return acc;
    }

    let value;

    if (config[key]?.dataType === DataType.LIST) {
      value = fieldValue ? { value: fieldValue, label: fieldValue } : null;
    } else if (config[key]?.dataType === DataType.MULTI_SELECT_LIST) {
      if (typeof fieldValue === 'string') {
        value = fieldValue ? fieldValue?.split(':').map((e: string) => ({ label: e, value: e })) : [];
      }
    } else if (config[key]?.dataType === DataType.CHECKBOX) {
      value = fieldValue === 'true' || fieldValue === true;
    } else if (config[key]?.dataType === DataType.DATE && fieldValue) {
      value = new Date(fieldValue as string);
    } else {
      value = fieldValue;
    }

    acc[key] = value;

    return acc;
  }, {} as FieldValues) ?? {};

const getDBOptionAdditionalFieldsFormValues = (additionalFields: IDBOptionAdditionalField[]) =>
  additionalFields?.reduce((acc, field) => {
    const { fieldId, fieldCode, optionId, fieldValue1, fieldValue2 } = field;
    const key = `${FieldPrefix.additionalField}${fieldId}`;
    acc[key] = optionId ? { value: optionId, label: fieldCode, name: fieldValue1, name2: fieldValue2 } : null;

    return acc;
  }, {} as FieldValues) ?? {};

export function getItemValues(item: Partial<IItemResponse>, additionalFieldsConfigMap: IAdditionalFieldsConfigMap) {
  const {
    commodityCode,
    countryOfOriginCode,
    countryOfOriginName,
    packageTypeCode,
    packageTypeName,
    requestedUomCode,
    requestedUomCode2,
    requestedUomName,
    requestedUomName2,
    approvedUomCode,
    approvedUomCode2,
    approvedUomName,
    approvedUomName2,
    additionalFields,
    dbOptionAdditionalFields,
    ...rest
  } = item;

  return {
    ...formInitialValues,
    ...rest,
    [ItemsTabFields.commodityCode]: getSelectValue({ value: commodityCode, description: rest.commodityDescription }),
    [ItemsTabFields.countryOfOriginCode]: getSelectValue({
      value: countryOfOriginCode,
      tagLabel: countryOfOriginName,
    }),
    [ItemsTabFields.packageTypeCode]: getSelectValue({
      value: packageTypeCode,
      tagLabel: packageTypeName,
    }),
    [ItemsTabFields.requestedUomCode]: getSelectValue({
      value: requestedUomCode,
      description: requestedUomName,
    }),
    [ItemsTabFields.requestedUomCode2]: getSelectValue({
      value: requestedUomCode2,
      description: requestedUomName2,
    }),
    [ItemsTabFields.approvedUomCode]: getSelectValue({
      value: approvedUomCode ?? requestedUomCode,
      description: approvedUomName ?? requestedUomName,
    }),
    [ItemsTabFields.approvedUomCode2]: getSelectValue({
      value: approvedUomCode2 ?? requestedUomCode2,
      description: approvedUomName2 ?? requestedUomName2,
    }),
    additionalFields: getAdditionalFieldsFormValues(additionalFields ?? [], additionalFieldsConfigMap),
    dbOptionAdditionalFields: getDBOptionAdditionalFieldsFormValues(dbOptionAdditionalFields ?? []),
  };
}

export function getAttachmentValues({ name, ...attachedDocument }: IAttachedDocumentResponse) {
  return {
    ...attachedDocument,
    [AttachedDocumentsTabFields.code]: getSelectValue({
      value: attachedDocument.code,
      tagLabel: name,
    }),
  };
}

export function getAttachmentValuesFromConfigs(attachedDocuments: IAttachedDocumentConfig[], withId = false) {
  return attachedDocuments.map(({ code, description }) => ({
    code: getSelectValue({
      value: code,
      tagLabel: description,
    }),
    referenceNumber: '',
    attachmentDate: null,
    fileUrl: '',
    ...(withId && { id: crypto.randomUUID() }),
  }));
}

export function getFormValuesFromLicenseType(licenseType: ILicenseTypeResponse) {
  const { flow, typeOfUse, attachedDocuments } = licenseType;

  return {
    [HeaderTabFields.typeOfUse]: getSelectValue({
      value: typeOfUse,
    }),
    [HeaderTabFields.flow]: flow,
    [ArrayFields.attachedDocuments]: getAttachmentValuesFromConfigs(attachedDocuments),
    [ArrayFields.items]: [],
  };
}

export function getAdditionalFieldsConfigMap(additionalFieldsConfig: IAdditionalFieldConfig[]) {
  return additionalFieldsConfig.reduce((acc, obj) => {
    acc[`${FieldPrefix.additionalField}${obj.id}`] = obj;

    return acc;
  }, {} as IAdditionalFieldsConfigMap);
}

function getFormValuesFromLicense(lpcoResponse: ILpcoResponse, selectedLicenseType: ILicenseTypeResponse) {
  const {
    licenseTypeCode,
    licenseTypeName,
    additionalFields: additionalFieldsConfig,
    featureFlags,
  } = selectedLicenseType;
  const additionalFieldsConfigMap = getAdditionalFieldsConfigMap(additionalFieldsConfig);

  const {
    countryOfDestinationCode,
    countryOfDestinationName,
    typeOfUse,
    termsOfDeliveryCode,
    termsOfDeliveryDescription,
    invoiceCurrencyCode,
    invoiceExchangeRate,
    placeOfLoadingCode,
    placeOfLoadingName,
    placeOfUnloadingCode,
    placeOfUnloadingName,
    companyCode,
    companyDescription,
    beneficiaries,
    items,
    fees,
    attachedDocuments,
    additionalFields,
    dbOptionAdditionalFields,
    departmentOfficeCode,
    departmentOfficeName,
    customsOfficeCode,
    customsOfficeName,
    transitCountryCode,
    transitCountryName,
    meanOfTransportCode,
    meanOfTransportName,
    ...rest
  } = lpcoResponse;

  return {
    ...rest,
    itemTabEnabled: featureFlags.itemTabEnabled,
    [HeaderTabFields.licenseType]: getSelectValue({
      value: licenseTypeCode,
      label: licenseTypeName,
      tagLabel: licenseTypeCode,
    }),

    [HeaderTabFields.countryOfDestinationCode]: getSelectValue({
      value: countryOfDestinationCode,
      tagLabel: countryOfDestinationName,
    }),

    [HeaderTabFields.departmentOfficeCode]: getSelectValue({
      value: departmentOfficeCode,
      tagLabel: departmentOfficeName,
    }),

    [HeaderTabFields.customsOfficeCode]: getSelectValue({
      value: customsOfficeCode,
      tagLabel: customsOfficeName,
    }),

    [HeaderTabFields.typeOfUse]: getSelectValue({
      value: typeOfUse,
    }),

    [HeaderTabFields.termsOfDeliveryCode]: getSelectValue({
      value: termsOfDeliveryCode,
      tagLabel: termsOfDeliveryDescription ?? '',
    }),

    [HeaderTabFields.invoiceCurrencyCode]: getSelectValue({
      value: invoiceCurrencyCode,
      tagLabel: invoiceExchangeRate ? String(invoiceExchangeRate) : '',
    }),

    [HeaderTabFields.placeOfLoadingCode]: getSelectValue({
      value: placeOfLoadingCode,
      tagLabel: placeOfLoadingName,
    }),

    [HeaderTabFields.placeOfUnloadingCode]: getSelectValue({
      value: placeOfUnloadingCode,
      tagLabel: placeOfUnloadingName,
    }),

    [NamesAndPartiesTabFields.companyCode]: getSelectValue({
      value: companyCode,
      tagLabel: companyDescription,
    }),

    [ConsignmentTabFields.transitCountryCode]: getSelectValue({
      value: transitCountryCode,
      tagLabel: transitCountryName,
    }),

    [ConsignmentTabFields.meanOfTransportCode]: getSelectValue({
      value: meanOfTransportCode,
      tagLabel: meanOfTransportName,
    }),

    [ArrayFields.beneficiaries]: beneficiaries.map((beneficiary) => ({
      ...beneficiary,
      [BeneficiaryFields.code]: getSelectValue({ value: beneficiary.code }),
    })),

    [ArrayFields.items]: items.map((item) => getItemValues(item, additionalFieldsConfigMap)),

    [ArrayFields.fees]: groupByBillNumber(fees),

    [ArrayFields.attachedDocuments]: attachedDocuments.map(getAttachmentValues),
    additionalFields: getAdditionalFieldsFormValues(additionalFields, additionalFieldsConfigMap),
    dbOptionAdditionalFields: getDBOptionAdditionalFieldsFormValues(dbOptionAdditionalFields),
  };
}

export default getFormValuesFromLicense;
